import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseSpaceReadModel, SpaceDiscriminator, useGetSpacesQuery } from '@client/api';

import { useSearching } from '../../hooks';
import { Autocomplete, AutocompleteProps } from '../../ui';
import { combineStrings, isDefinedString } from '../../utils';

interface ParentSpaceAutocompleteProps extends Omit<AutocompleteProps<BaseSpaceReadModel>, 'options'> {
  rootId?: string;
  spaceId?: string;
  discriminator?: SpaceDiscriminator;
}

export function ParentSpaceAutocomplete({ rootId, spaceId, discriminator, ...props }: ParentSpaceAutocompleteProps) {
  const { t: tType } = useTranslation('type');

  const { debouncedSearch, updateSearch, searchDebouncing } = useSearching();

  const allowedDiscriminators: SpaceDiscriminator[] | undefined = useMemo(() => {
    if (discriminator === 'Room') {
      return ['ResidentialUnit'];
    } else if (discriminator === 'ParkingSpot') {
      return ['Building', 'ResidentialUnit', 'CommercialSpace', 'Generic']
    }
    return ['Building', 'Generic'];
  }, [discriminator]);

  const { currentData, isFetching } = useGetSpacesQuery({
    "rootId.Equal": rootId,
    search: debouncedSearch ?? undefined,
    "discriminator.Contains": allowedDiscriminators
  }, { skip: !rootId });

  // exclude the space that is currently being edited in case its edit
  const options = useMemo(
    () => currentData?.items.filter((x) => x.id !== spaceId) ?? [],
    [currentData?.items, spaceId]
  );
  const getLabel = useCallback(
    (space?: BaseSpaceReadModel) => {
      if (!space?.name) {
        return '';
      }
      return (
        combineStrings(
          ' ― ',
          space?.name,
          isDefinedString(space?.discriminator)
            ? tType(`SpaceDiscriminator.${space?.discriminator}`)
            : tType('SpaceDiscriminator.Generic')
        ) ?? ''
      );
    },
    [tType]
  );

  return (
    <Autocomplete<BaseSpaceReadModel>
      options={options}
      getSelectedLabel={getLabel}
      getOptionLabel={getLabel}
      onInput={updateSearch}
      loading={isFetching || searchDebouncing}
      {...props}
      disabled={props.disabled || !rootId}
    />
  );
};
